import { InputNumber } from "primereact/inputnumber";
import { ProgressSpinner } from "primereact/progressspinner";
import { ToggleButton } from "primereact/togglebutton";
import { Suspense } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { type UsageLimitedInput } from "@relay/discountActionForm_EditDiscountActionMutation.graphql";
import { type UsageLimitationInput_Query } from "@relay/UsageLimitationInput_Query.graphql";
import { AccountsSelectWithId } from "./AccountsSelectWithId";
import { ProductsSelect } from "./ProductsSelect";

type OwnProps = {
	fieldValue?: UsageLimitedInput;
	update: (discountTypeKind?: UsageLimitedInput) => void;
	initialValues: UsageLimitedInput | undefined;
};

const QUERY = graphql`
	query UsageLimitationInput_Query($accountIds: [ID!]!, $productIds: [ID!]!) {
		...AccountsSelectWithId_QueryFragment @arguments(ids: $accountIds)
		...ProductsSelect_QueryFragment @arguments(productIds: $productIds)
	}
`;

export const UsageLimitationInput = ({ fieldValue, update, initialValues }: OwnProps) => {
	const { t } = useTranslation("common");
	const query = useLazyLoadQuery<UsageLimitationInput_Query>(QUERY, {
		accountIds: fieldValue?.onlyForAccountIds || [],
		productIds: fieldValue?.onlyForProductIds || [],
	});

	return (
		<div>
			<ToggleButton
				onLabel={t("actions.yes")}
				offLabel={t("actions.no")}
				className="w-8rem"
				checked={fieldValue !== undefined}
				onChange={(e) => {
					if (e.value) {
						update(
							initialValues ?? {
								maxAmountOfUsages: undefined,
								maxAmountOfUsagesPerAccount: undefined,
								onlyForAccountIds: [],
								onlyForProductIds: [],
							},
						);
					} else {
						update(undefined);
					}
				}}
			/>
			{fieldValue && (
				<div className="mt-6">
					<span className="p-float-label">
						<InputNumber
							max={1000}
							min={0}
							value={fieldValue?.maxAmountOfUsages || undefined}
							onChange={(e) => {
								update({ ...fieldValue, maxAmountOfUsages: e.value });
							}}
						/>
						<label htmlFor="maxAmountOfUsages">
							{t("accounts.maxUsages", { max: 1000 })}
						</label>
					</span>
					<span className="mt-4 p-float-label">
						<InputNumber
							max={1000}
							min={0}
							value={fieldValue?.maxAmountOfUsagesPerAccount || undefined}
							onChange={(e) => {
								update({
									...fieldValue,
									maxAmountOfUsagesPerAccount: e.value,
								});
							}}
						/>
						<label htmlFor="maxAmountOfUsagesPerAccount">
							{t("accounts.maxUsagesPerUser", { max: 1000 })}
						</label>
					</span>
					<span className="mt-4 p-float-label">
						<Suspense fallback={<ProgressSpinner />}>
							<AccountsSelectWithId
								fieldValue={fieldValue.onlyForAccountIds.map((l) => l) || []}
								onChange={(accountIds) => {
									update({
										...fieldValue,
										onlyForAccountIds: accountIds,
									});
								}}
								queryFragmentRef={query}
							/>
						</Suspense>
						<label htmlFor="onlyForAccountIds">{t("accounts.onlyForAccounts")}</label>
					</span>
					<span className="mt-4 p-float-label">
						<Suspense fallback={<ProgressSpinner />}>
							<ProductsSelect
								fieldValue={fieldValue.onlyForProductIds.map((l) => l) || []}
								onChange={(productIds) => {
									update({
										...fieldValue,
										onlyForProductIds: productIds,
									});
								}}
								queryFragmentRef={query}
							/>
						</Suspense>
						<label htmlFor="onlyForProductIds">{t("accounts.onlyForCourses")}</label>
					</span>
				</div>
			)}
		</div>
	);
};
